import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './api';
import { Toaster } from 'sonner';
import Login from './pages/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Profiles from './pages/Profiles';
import Documents from './pages/Documents';
import MasterList from './pages/MasterList';
import ExternalDocuments from './pages/ExternalDocuments';
import ChangeControl from './pages/ChangeControl';
import NonConformities from './pages/NonConformities';
import SACPM from './pages/SACPM';
import Audits from './pages/Audits';
import AuditAgenda from './pages/AuditAgenda';
import AuditReport from './pages/AuditReport';
import AuditSchedule from './pages/AuditSchedule';
import PublicAudit from './pages/PublicAudit';
import Indicators from './pages/Indicators';
import Risks from './pages/Risks';
import SWOT from './pages/SWOT';
import TopManagement from './pages/TopManagement';
import MeetingMinutes from './pages/MeetingMinutes';
import SignDocument from './pages/SignDocument';
// Phase 5 - Operational Modules
import Calibrations from './pages/Calibrations';
import Suppliers from './pages/Suppliers';
import Inspections from './pages/Inspections';
import SST from './pages/SST';
import LaborSafety from './pages/LaborSafety';
// Phase 6 - HR and Customer Modules
import Trainings from './pages/Trainings';
import Complaints from './pages/Complaints';
// Phase 7 - Notifications and Settings
import Settings from './pages/Settings';
// Cadastros
import Sectors from './pages/Sectors';
import Types from './pages/Types';
import Processes from './pages/Processes';
import Certificates from './pages/Certificates';
import MyProfile from './pages/MyProfile';
import AuditTrail from './pages/AuditTrail';
import PeriodicReview from './pages/PeriodicReview';
import AboutSystem from './pages/AboutSystem';
// Regulatório - Controle
import Controle from './pages/Controle';
import ClientQualification from './pages/ClientQualification';
import ClassificacaoCliente from './pages/ClassificacaoCliente';
import ClientRequirementAnalysis from './pages/ClientRequirementAnalysis';
import ClientRequirementAnalysisForm from './pages/ClientRequirementAnalysisForm';
import Qualificacoes from './pages/Qualificacoes';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !user?.is_admin) {
    const requiredCodes = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasAccess = requiredCodes.some(code =>
      user?.permissions?.some(p => p.permission?.module_code === code && p.can_view)
    );
    if (!hasAccess) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  const routes = useMemo(() => (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      {/* Public e-signature page — no login required, access controlled by the token in the URL */}
      <Route path="/assinar/:module/:token" element={<SignDocument />} />
      {/* Public client classification form — no login required, link sent by e-mail */}
      <Route path="/classificacao/:token" element={<ClassificacaoCliente />} />
      {/* Public client requirement analysis form — no login required, link sent by e-mail */}
      <Route path="/analise-critica/:token" element={<ClientRequirementAnalysisForm />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="usuarios"         element={<ProtectedRoute requiredPermission="users"><Users /></ProtectedRoute>} />
        <Route path="perfis"           element={<ProtectedRoute requiredPermission="profiles"><Profiles /></ProtectedRoute>} />
        <Route path="documentos"       element={<ProtectedRoute requiredPermission="documents"><Documents /></ProtectedRoute>} />
        <Route path="lista-mestra"     element={<ProtectedRoute requiredPermission="master_list"><MasterList /></ProtectedRoute>} />
        <Route path="documentos-externos" element={<ProtectedRoute requiredPermission="external_documents"><ExternalDocuments /></ProtectedRoute>} />
        <Route path="controle-mudancas" element={<ProtectedRoute requiredPermission="change_control"><ChangeControl /></ProtectedRoute>} />
        <Route path="nao-conformidades" element={<ProtectedRoute requiredPermission="non_conformities"><NonConformities /></ProtectedRoute>} />
        <Route path="sacpm"            element={<ProtectedRoute requiredPermission="sacpm"><SACPM /></ProtectedRoute>} />
        <Route path="auditorias"       element={<ProtectedRoute requiredPermission="audits"><Audits /></ProtectedRoute>} />
        <Route path="auditorias/agenda/:auditId" element={<ProtectedRoute requiredPermission="audits"><AuditAgenda /></ProtectedRoute>} />
        <Route path="auditorias/relatorio/:auditId" element={<ProtectedRoute requiredPermission="audits"><AuditReport /></ProtectedRoute>} />
        <Route path="auditorias/agenda-geral" element={<ProtectedRoute requiredPermission="audits"><AuditSchedule /></ProtectedRoute>} />
        <Route path="indicadores"      element={<ProtectedRoute requiredPermission="indicators"><Indicators /></ProtectedRoute>} />
        <Route path="riscos"           element={<ProtectedRoute requiredPermission="risks"><Risks /></ProtectedRoute>} />
        <Route path="swot"             element={<ProtectedRoute requiredPermission="swot"><SWOT /></ProtectedRoute>} />
        <Route path="alta-direcao"     element={<ProtectedRoute requiredPermission="top_management"><TopManagement /></ProtectedRoute>} />
        <Route path="ata-reuniao"      element={<ProtectedRoute requiredPermission="meeting_minutes"><MeetingMinutes /></ProtectedRoute>} />
        <Route path="calibracoes"      element={<ProtectedRoute requiredPermission="calibrations"><Calibrations /></ProtectedRoute>} />
        <Route path="fornecedores"     element={<ProtectedRoute requiredPermission="suppliers"><Suppliers /></ProtectedRoute>} />
        <Route path="auto-inspecao"    element={<ProtectedRoute requiredPermission="inspections"><Inspections /></ProtectedRoute>} />
        <Route path="sst"              element={<ProtectedRoute requiredPermission="sst"><SST /></ProtectedRoute>} />
        <Route path="seguranca-trabalho" element={<ProtectedRoute requiredPermission="sst"><LaborSafety /></ProtectedRoute>} />
        <Route path="treinamentos"     element={<ProtectedRoute requiredPermission="trainings"><Trainings /></ProtectedRoute>} />
        <Route path="reclamacoes"      element={<ProtectedRoute requiredPermission="complaints"><Complaints /></ProtectedRoute>} />
        <Route path="configuracoes"    element={<ProtectedRoute requiredPermission="settings"><Settings /></ProtectedRoute>} />
        <Route path="controle" element={<ProtectedRoute requiredPermission="controle"><Controle /></ProtectedRoute>} />
        <Route path="cliente-qualificado" element={<ProtectedRoute requiredPermission="client_qualification"><ClientQualification /></ProtectedRoute>} />
        <Route path="analise-critica" element={<ProtectedRoute requiredPermission="client_requirement_analysis"><ClientRequirementAnalysis /></ProtectedRoute>} />
        <Route path="qualificacoes" element={<ProtectedRoute requiredPermission="qualificacoes"><Qualificacoes /></ProtectedRoute>} />
        <Route path="cadastros/setores" element={<ProtectedRoute requiredPermission="sectors"><Sectors /></ProtectedRoute>} />
        <Route path="cadastros/tipos" element={<ProtectedRoute requiredPermission="types"><Types /></ProtectedRoute>} />
        <Route path="cadastros/processos" element={<ProtectedRoute requiredPermission="processes"><Processes /></ProtectedRoute>} />
        <Route path="cadastros/certificacoes" element={<ProtectedRoute requiredPermission="certificates"><Certificates /></ProtectedRoute>} />
        <Route path="meu-perfil" element={<MyProfile />} />
        <Route path="trilha-auditoria"  element={<ProtectedRoute><AuditTrail /></ProtectedRoute>} />
        <Route path="revisao-periodica" element={<ProtectedRoute><PeriodicReview /></ProtectedRoute>} />
        <Route path="sobre"             element={<ProtectedRoute><AboutSystem /></ProtectedRoute>} />
      </Route>
      <Route path="/compartilhado/:token" element={<PublicAudit />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  ), [isAuthenticated]);

  return routes;
}

// Coming Soon Component for future modules
const ComingSoon = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-full py-20">
    <div className="text-gray-400 text-6xl mb-4">🚧</div>
    <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
    <p className="text-gray-400">Este módulo será implementado em breve</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              background: '#1f2937',
              border: '1px solid #374151',
              color: '#fff',
            },
          }}
        />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
